const CNPJ_BODY_LENGTH = 12;
const CNPJ_LENGTH = 14;
const FIRST_DIGIT_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const SECOND_DIGIT_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

const toAlphanumericUppercase = (value = ""): string =>
  String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const getBody = (value = ""): string => toAlphanumericUppercase(value).slice(0, CNPJ_BODY_LENGTH);

const getDigits = (value = ""): string =>
  toAlphanumericUppercase(value)
    .slice(CNPJ_BODY_LENGTH)
    .replace(/\D/g, "")
    .slice(0, 2);

const getCharacterValue = (character: string): number => character.charCodeAt(0) - 48;

const calculateCheckDigit = (value: string, weights: number[]): number => {
  const sum = value
    .split("")
    .reduce((accumulator, character, index) => accumulator + getCharacterValue(character) * weights[index], 0);

  const remainder = sum % 11;

  return remainder < 2 ? 0 : 11 - remainder;
};

export const normalizeAlphanumericCNPJ = (value = ""): string => {
  const body = getBody(value);
  const digits = getDigits(value);

  return `${body}${digits}`;
};

export const formatAlphanumericCNPJ = (value = ""): string => {
  const normalized = normalizeAlphanumericCNPJ(value);

  if (!normalized) {
    return "";
  }

  const part1 = normalized.slice(0, 2);
  const part2 = normalized.slice(2, 5);
  const part3 = normalized.slice(5, 8);
  const part4 = normalized.slice(8, 12);
  const part5 = normalized.slice(12, 14);

  let formatted = part1;

  if (part2) {
    formatted += `.${part2}`;
  }

  if (part3) {
    formatted += `.${part3}`;
  }

  if (part4) {
    formatted += `/${part4}`;
  }

  if (part5) {
    formatted += `-${part5}`;
  }

  return formatted;
};

export const validateAlphanumericCNPJ = (value = ""): boolean => {
  const normalized = normalizeAlphanumericCNPJ(value);

  if (normalized.length !== CNPJ_LENGTH) {
    return false;
  }

  // Sequências repetidas (ex.: 00.000.000/0000-00) passam no DV, mas não são CNPJ válidos.
  if (/^(.)\1+$/.test(normalized)) {
    return false;
  }

  const body = normalized.slice(0, CNPJ_BODY_LENGTH);
  const digits = normalized.slice(CNPJ_BODY_LENGTH);

  if (!/^\d{2}$/.test(digits)) {
    return false;
  }

  const firstDigit = calculateCheckDigit(body, FIRST_DIGIT_WEIGHTS);
  const secondDigit = calculateCheckDigit(`${body}${firstDigit}`, SECOND_DIGIT_WEIGHTS);

  return digits === `${firstDigit}${secondDigit}`;
};
